import { useState } from "react";
import { useCustomMutation, useInvalidate, useGo } from "@refinedev/core";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Users, Plus, Trash2, UserCheck, Loader2, Edit2 } from "lucide-react";
import type { Subscription, SubscriptionMember, FamilyMemberStatus } from "../types";

interface FamilyMembersSectionProps {
  subscription: Subscription;
}

/**
 * Компонент для управления членами семейной подписки
 */
export function FamilyMembersSection({ subscription }: FamilyMembersSectionProps) {
  const go = useGo();
  const invalidate = useInvalidate();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditSlotsDialogOpen, setIsEditSlotsDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<SubscriptionMember | null>(null);
  const [addInput, setAddInput] = useState("");
  const [newSlots, setNewSlots] = useState(String(subscription.subscriptionPlan?.familySlots ?? 1));
  const [removeReason, setRemoveReason] = useState("");

  const { mutate: addMember, mutation: addMemberMutation } = useCustomMutation();
  const { mutate: removeMember, mutation: removeMemberMutation } = useCustomMutation();
  const { mutate: updateSlots, mutation: updateSlotsMutation } = useCustomMutation();

  const isAddingMember = addMemberMutation.isPending;
  const isRemovingMember = removeMemberMutation.isPending;
  const isUpdatingSlots = updateSlotsMutation.isPending;

  const isFamily = subscription.subscriptionPlan?.isFamily ?? false;
  const familySlots = subscription.subscriptionPlan?.familySlots ?? 0;
  const activeMembers = subscription.familyMembers?.filter(m => m.status === 'ACTIVE') ?? [];
  const usedSlots = activeMembers.length;
  const availableSlots = familySlots - usedSlots;

  if (!isFamily) {
    return null;
  }

  const handleAddMember = () => {
    if (!addInput.trim()) {return;}

    addMember(
      {
        url: `/family-subscriptions/${subscription.id}/members`,
        method: "post",
        values: { telegramIdOrUsername: addInput.trim() },
      },
      {
        onSuccess: () => {
          setIsAddDialogOpen(false);
          setAddInput("");
          invalidate({ resource: "subscriptions", invalidates: ["detail"] });
        },
      }
    );
  };

  const handleRemoveMember = () => {
    if (!memberToRemove) {return;}

    removeMember(
      {
        url: `/family-subscriptions/members/${memberToRemove.id}`,
        method: "delete",
        values: { reason: removeReason || undefined },
      },
      {
        onSuccess: () => {
          setMemberToRemove(null);
          setRemoveReason("");
          invalidate({ resource: "subscriptions", invalidates: ["detail"] });
        },
      }
    );
  };

  const handleUpdateSlots = () => {
    const slots = parseInt(newSlots, 10);
    if (isNaN(slots) || slots < 1) {return;}

    updateSlots(
      {
        url: `/family-subscriptions/${subscription.id}/slots`,
        method: "put",
        values: { familySlots: slots },
      },
      {
        onSuccess: () => {
          setIsEditSlotsDialogOpen(false);
          invalidate({ resource: "subscriptions", invalidates: ["detail"] });
        },
      }
    );
  };

  const getStatusBadge = (status: FamilyMemberStatus) => {
    switch (status) {
      case "ACTIVE":
        return <Badge className="bg-green-500/10 text-green-500">Активен</Badge>;
      case "PENDING":
        return <Badge variant="outline">Ожидает</Badge>;
      case "REMOVED":
        return <Badge variant="secondary">Удалён</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getMemberName = (member: SubscriptionMember) => {
    if (!member.user) {return `Пользователь #${member.userId}`;}
    const name = [member.user.firstName, member.user.lastName].filter(Boolean).join(" ");
    return name || member.user.username || `ID: ${member.user.telegramId}`;
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Семейная подписка
            </CardTitle>
            <CardDescription>
              Использовано {usedSlots} из {familySlots} мест
              {availableSlots > 0 && ` (${availableSlots} свободно)`}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setNewSlots(String(familySlots));
                setIsEditSlotsDialogOpen(true);
              }}
            >
              <Edit2 className="h-4 w-4 mr-2" />
              Изменить лимит
            </Button>
            <Button
              size="sm"
              onClick={() => setIsAddDialogOpen(true)}
              disabled={availableSlots <= 0}
            >
              <Plus className="h-4 w-4 mr-2" />
              Добавить
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {(subscription.familyMembers?.length ?? 0) > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Участник</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead>Добавлен</TableHead>
                  <TableHead className="w-[100px]">Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscription.familyMembers?.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{getMemberName(member)}</span>
                        {member.user?.username && (
                          <span className="text-xs text-muted-foreground">
                            @{member.user.username}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(member.status)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(member.addedAt)}
                      {member.removedAt && (
                        <div className="text-xs">
                          Удалён: {formatDate(member.removedAt)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => go({ to: `/users/show/${member.userId}` })}
                        >
                          <UserCheck className="h-4 w-4" />
                        </Button>
                        {member.status === "ACTIVE" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setMemberToRemove(member)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Нет членов семейной подписки</p>
              <p className="text-sm">Добавьте участников, чтобы они получили доступ</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Диалог добавления участника */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Добавить участника</DialogTitle>
            <DialogDescription>
              Введите Telegram ID или username (с @) пользователя, которого хотите добавить.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={addInput}
              onChange={(e) => setAddInput(e.target.value)}
              placeholder="@username или 123456789"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleAddMember} disabled={isAddingMember || !addInput.trim()}>
              {isAddingMember && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Добавить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Диалог изменения количества слотов */}
      <Dialog open={isEditSlotsDialogOpen} onOpenChange={setIsEditSlotsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Изменить лимит мест</DialogTitle>
            <DialogDescription>
              Укажите новое количество мест для семейной подписки.
              Текущее использование: {usedSlots} из {familySlots}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              type="number"
              min="1"
              value={newSlots}
              onChange={(e) => setNewSlots(e.target.value)}
            />
            {parseInt(newSlots, 10) < usedSlots && (
              <p className="text-sm text-warning mt-2">
                Внимание: новый лимит меньше текущего использования.
                Существующие участники сохранят доступ.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditSlotsDialogOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleUpdateSlots}
              disabled={isUpdatingSlots || !newSlots || parseInt(newSlots, 10) < 1}
            >
              {isUpdatingSlots && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Диалог удаления участника */}
      <AlertDialog open={!!memberToRemove} onOpenChange={() => setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить участника?</AlertDialogTitle>
            <AlertDialogDescription>
              {memberToRemove && (
                <>
                  Вы уверены, что хотите удалить {getMemberName(memberToRemove)} из семейной подписки?
                  Этот пользователь потеряет доступ к контенту.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Input
              placeholder="Причина удаления (опционально)"
              value={removeReason}
              onChange={(e) => setRemoveReason(e.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRemoveReason("")}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isRemovingMember}
            >
              {isRemovingMember && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
